import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, SlidersHorizontal, X } from 'lucide-react';
import { useBrands, useProducts } from '../hooks/use-catalog.js';
import { StorefrontEmptyState, StorefrontErrorState } from '../../components/states.js';
import { ProductCard, ProductCardSkeleton } from './product-card.js';
import { ProductFilters, type ListingFilterState } from './product-filters.js';
import type { ProductListParams, ProductSortField, SortDirection } from '../types/catalog.js';

const PAGE_SIZE = 24;

/** Shared with the loading-skeleton grid below so the real cards and their placeholders never drift out of alignment. */
const PRODUCT_GRID_CLASSES = 'grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 lg:grid-cols-4';

export interface ProductListingBaseParams {
  categoryId?: string;
  search?: string;
}

export interface ProductListingProps {
  baseParams: ProductListingBaseParams;
  /** Announced to assistive tech / used for the empty-state copy, e.g. "this category" or "your search". */
  emptyStateDescription?: string;
}

function readFilters(searchParams: URLSearchParams): ListingFilterState {
  const priceMin = searchParams.get('priceMin');
  const priceMax = searchParams.get('priceMax');
  return {
    brandId: searchParams.get('brand') ?? undefined,
    priceMinPaise: priceMin !== null ? Number(priceMin) : undefined,
    priceMaxPaise: priceMax !== null ? Number(priceMax) : undefined,
    inStockOnly: searchParams.get('inStock') === '1' || undefined,
    isFeatured: searchParams.get('featured') === '1' || undefined,
  };
}

function readSort(searchParams: URLSearchParams): { field: ProductSortField; direction: SortDirection } {
  const field = searchParams.get('sort');
  const direction = searchParams.get('dir');
  return {
    field: (field === 'name' || field === 'createdAt' || field === 'publishedAt' || field === 'popularity' ? field : 'createdAt'),
    direction: direction === 'asc' ? 'asc' : 'desc',
  };
}

function readPage(searchParams: URLSearchParams): number {
  const raw = Number(searchParams.get('page') ?? '0');
  return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
}

/**
 * The shared listing UI behind `ShopPage`, `CategoryPage`, and
 * `SearchPage` -- URL-synchronized filters/sort/page, a desktop sidebar
 * + mobile drawer, the product grid, and bounded pagination. Every
 * filter/sort/page change re-issues a fresh `GET /catalog/products`
 * call via `useProducts` -- never fetches "all products" and slices
 * client-side. The backend list endpoint returns no total count, so
 * pagination follows the same "disable Next when the page came back
 * shorter than the limit" convention as the admin products list
 * (`apps/web/src/features/admin/catalog/pages/products-list-page.tsx`).
 */
export function ProductListing({ baseParams, emptyStateDescription }: ProductListingProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const drawerRef = useRef<HTMLDivElement>(null);
  const drawerOpen = searchParams.get('filters') === 'open';

  const filters = readFilters(searchParams);
  const sort = readSort(searchParams);
  const page = readPage(searchParams);

  const { data: brands } = useBrands();

  const params: ProductListParams = useMemo(
    () => ({
      categoryId: baseParams.categoryId,
      search: baseParams.search,
      brandId: filters.brandId,
      priceMinPaise: filters.priceMinPaise,
      priceMaxPaise: filters.priceMaxPaise,
      inStockOnly: filters.inStockOnly,
      isFeatured: filters.isFeatured,
      sortField: sort.field,
      sortDirection: sort.direction,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [
      baseParams.categoryId,
      baseParams.search,
      filters.brandId,
      filters.priceMinPaise,
      filters.priceMaxPaise,
      filters.inStockOnly,
      filters.isFeatured,
      sort.field,
      sort.direction,
      page,
    ],
  );

  const { data, isLoading, isError, refetch, isFetching } = useProducts(params);
  const rows = data ?? [];

  function updateParams(mutator: (next: URLSearchParams) => void, resetPage = true) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      mutator(next);
      if (resetPage) next.delete('page');
      return next;
    });
  }

  function handleFiltersChange(next: ListingFilterState) {
    updateParams((params) => {
      if (next.brandId) params.set('brand', next.brandId);
      else params.delete('brand');

      if (next.priceMinPaise !== undefined) params.set('priceMin', String(next.priceMinPaise));
      else params.delete('priceMin');

      if (next.priceMaxPaise !== undefined) params.set('priceMax', String(next.priceMaxPaise));
      else params.delete('priceMax');

      if (next.inStockOnly) params.set('inStock', '1');
      else params.delete('inStock');

      if (next.isFeatured) params.set('featured', '1');
      else params.delete('featured');
    });
  }

  function handleSortChange(field: ProductSortField, direction: SortDirection) {
    updateParams((params) => {
      params.set('sort', field);
      params.set('dir', direction);
    });
  }

  function goToPage(next: number) {
    updateParams((params) => {
      if (next > 0) params.set('page', String(next));
      else params.delete('page');
    }, false);
  }

  function openDrawer() {
    updateParams((params) => params.set('filters', 'open'), false);
  }

  function closeDrawer() {
    updateParams((params) => params.delete('filters'), false);
  }

  useEffect(() => {
    if (!drawerOpen) return;

    const container = drawerRef.current;
    const focusables = container?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, select');
    focusables?.[0]?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeDrawer();
        return;
      }
      if (event.key !== 'Tab' || !container) return;
      const items = Array.from(container.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, select'));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
      <aside className="hidden w-64 shrink-0 rounded-2xl border border-gray-100 bg-white p-4 lg:block">
        <ProductFilters
          brands={brands ?? []}
          value={filters}
          onChange={handleFiltersChange}
          sortField={sort.field}
          sortDirection={sort.direction}
          onSortChange={handleSortChange}
          idPrefix="desktop"
        />
      </aside>

      <div className="min-w-0 flex-1">
        <div className="mb-4 flex items-center justify-between lg:justify-end">
          <button
            type="button"
            onClick={openDrawer}
            className="focus-visible:ring-brand flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 lg:hidden"
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
            Filters &amp; sort
          </button>
        </div>

        {isLoading ? (
          <div className={PRODUCT_GRID_CLASSES} role="status" aria-label="Loading products">
            {Array.from({ length: PAGE_SIZE }).map((_, index) => (
              <ProductCardSkeleton key={index} />
            ))}
          </div>
        ) : isError ? (
          <StorefrontErrorState onRetry={() => void refetch()} />
        ) : rows.length === 0 ? (
          <StorefrontEmptyState
            title="No products found"
            description={emptyStateDescription ?? 'Try adjusting your filters or search terms.'}
          />
        ) : (
          <div className={PRODUCT_GRID_CLASSES} aria-busy={isFetching}>
            {rows.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
            {isFetching && rows.length > 0 && Array.from({ length: 2 }).map((_, index) => <ProductCardSkeleton key={`fetching-${index}`} />)}
          </div>
        )}

        {rows.length > 0 && (
          <nav aria-label="Pagination" className="mt-6 flex items-center justify-between text-sm text-gray-600">
            <button
              type="button"
              onClick={() => goToPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="focus-visible:ring-brand flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              Previous
            </button>
            <span>Page {page + 1}</span>
            <button
              type="button"
              onClick={() => goToPage(page + 1)}
              disabled={rows.length < PAGE_SIZE}
              className="focus-visible:ring-brand flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 disabled:opacity-40"
            >
              Next
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </nav>
        )}
      </div>

      {drawerOpen &&
        createPortal(
          <div className="fixed inset-0 z-40 flex lg:hidden">
            <div className="fixed inset-0 bg-black/40" onClick={closeDrawer} aria-hidden="true" />
            <div
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-label="Filters and sort"
              className="relative ml-auto flex h-full w-80 max-w-[88vw] flex-col gap-5 overflow-y-auto bg-white p-4 shadow-xl"
            >
              <div className="flex items-center justify-between">
                <p className="text-lg font-bold text-gray-900">Filters &amp; sort</p>
                <button
                  type="button"
                  onClick={closeDrawer}
                  aria-label="Close filters"
                  className="focus-visible:ring-brand flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              <ProductFilters
                brands={brands ?? []}
                value={filters}
                onChange={handleFiltersChange}
                sortField={sort.field}
                sortDirection={sort.direction}
                onSortChange={handleSortChange}
                idPrefix="mobile"
              />
              <button
                type="button"
                onClick={closeDrawer}
                className="bg-brand text-brand-foreground focus-visible:ring-brand mt-auto rounded-full px-4 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2"
              >
                Show results
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
